import {useEffect, useState} from "react";
import axios from "axios";
import {ApiResponseDto, BasePopupProps, NameWithId} from "../../../../utils/Models";
import {ApiUrl_Labs_GetLabList, ApiUrl_Orders_DelegateOrder} from "../../../../utils/Endpoints";
import {
    useFlintNotificationContext
} from "../../../../components/page_components/common/notifications/FlintNotificationContext";
import FlintLoadingWrapper from "../../../../components/page_components/common/loading/FlintLoadingWrapper";
import {useTranslation} from "react-i18next";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import FlintButton from "../../../../components/controls/FlintButton";
import FlintDropDownList from "../../../../components/controls/FlintDropDownList";
import {useOrderPreviewContext} from "../OrderPreviewContext";
import {useFlintDialogContext} from "../../../../components/page_components/common/dialogs/FlintDialogContextProvider";
import {DelegateOrderRequest, OrderPreviewModel, OrderPreviewModelStateType} from "../../order.models";

export default function DelegateOrderPanel(props: DelegateOrderPanelProps) {
    const {t} = useTranslation();
    const {state, handlers} = useDelegateOrderPanel(props);
    return (
        <FlintLoadingWrapper showLoading={state.isLoading.get} loadingWrapperClassName="d-grid h-100">
            <div>
                <div className={"alert alert-info"}>{t("ORDER_DELEGATE_INFO")}</div>
                <div className={"form-group"}>
                    {state.labList.get.length > 0 && 
                        <FlintDropDownList<NameWithId> label={t("COMMON_LABORATORY")} dataSource={state.labList.get}
                                                   onItemSelected={handlers.handleLabSelected}/>}
                </div>
            </div>
            <WindowActionsBar layout={"center"}>
                <FlintButton disabled={state.isLoading.get} themeColor={"secondary"}
                             onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton disabled={state.isLoading.get || !state.selectedLab.get} themeColor={"primary"}
                             onClick={handlers.handleConfirmed}>{t("Confirm")}</FlintButton>
            </WindowActionsBar>
        </FlintLoadingWrapper>
    )
}

export interface DelegateOrderPanelProps extends BasePopupProps {
    orderPreviewModelState: OrderPreviewModelStateType
}

function useDelegateOrderPanel(props: DelegateOrderPanelProps) {
    const [isLoading, setIsLoading] = useState(false);
    const {showNotification} = useFlintNotificationContext();
    const [labList, setLabList] = useState<NameWithId[]>([]);
    const [selectedLab, setSelectedLab] = useState<NameWithId>();
    const {showConfirmDialog} = useFlintDialogContext();
    const basicDataState = props.orderPreviewModelState.get.OrderBasicData;
    const {t} = useTranslation();
    useEffect(() => {
        loadLabList().then();
        if (!basicDataState.RootProjectLeader) {
            showConfirmDialog({
                content: (<div
                    dangerouslySetInnerHTML={{__html: t("ORDER_PREVIEW_DelegationNoProjectLeaderWarning_HTML")}}></div>),
                cancelButtonText: "Continue",
            })
        }
    }, [])

    function loadLabList() {
        setIsLoading(true);
        return axios.get<ApiResponseDto<NameWithId[]>>(ApiUrl_Labs_GetLabList)
            .then(res => {
                const currentLabId = props.orderPreviewModelState.get.Info.OrderCurrentLab.Id;
                const labList = res.data.Result.filter(x => x.Id != currentLabId);
                setLabList(labList);
            })
            .catch(err => showNotification(err))
            .finally(() => setIsLoading(false))
    }

    function handleCancelled() {
        props.OnCancelled();
    }

    function handleConfirmed() {
        const post: DelegateOrderRequest = {
            OrderGuid: basicDataState.Guid,
            TargetLabId: selectedLab?.Id!
        }
        setIsLoading(true);
        axios.post<ApiResponseDto<OrderPreviewModel>>(ApiUrl_Orders_DelegateOrder, post)
            .then(res => {
                props.orderPreviewModelState.setModel(res.data.Result);
                showNotification(t("ORDER_PREVIEW_DelegationCompleted"), {style: "success"})
                props.OnConfirmedAndSaved();
            })
            .catch(err => showNotification(err))
            .finally(() => setIsLoading(false))
    }

    function handleLabSelected(lab: NameWithId) {
        setSelectedLab(lab)
    }

    return {
        state: {
            isLoading: {get: isLoading},
            labList: {get: labList},
            selectedLab: {get: selectedLab},
        },
        handlers: {handleConfirmed, handleCancelled, handleLabSelected}
    }
}